import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Card, message, Spin, Modal } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, UserOutlined } from '@ant-design/icons';
import { getUser, updateUser } from './api';

const { Option } = Select;

interface UserFormProps {
  userId: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface UserData {
  email: string;
  roleId: number;
}

interface User {
  id: number;
  email: string;
  roleId: number;
  role: {
    id: number;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

const UserForm: React.FC<UserFormProps> = ({ userId, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadUser();
  }, [userId]);

  const loadUser = async () => {
    setInitialLoading(true);
    try {
      const user = await getUser(userId);
      form.setFieldsValue({
        email: user.email,
        roleId: user.roleId,
      });
      setHasChanges(false);
    } catch (error) {
      message.error('Ошибка загрузки пользователя');
      console.error(error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values: UserData) => {
    setLoading(true);
    try {
      await updateUser(userId, values);
      message.success('Пользователь успешно обновлен');
      setHasChanges(false);
      onSuccess();
    } catch (error: any) {
      message.error(error.message || 'Произошла ошибка');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      Modal.confirm({
        title: 'Подтверждение',
        content: 'У вас есть несохраненные изменения. Вы уверены, что хотите выйти?',
        okText: 'Да, выйти',
        cancelText: 'Отмена',
        onOk: () => {
          setHasChanges(false);
          onCancel();
        },
      });
    } else {
      onCancel();
    }
  };

  const handleValuesChange = () => {
    setHasChanges(true);
  };

  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            style={{ padding: 0, height: 'auto' }}
          />
          Редактировать пользователя
        </div>
      }
      style={{ maxWidth: 600, margin: '0 auto' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
      >
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Пожалуйста, введите email' },
            { type: 'email', message: 'Введите корректный email' }
          ]}
        >
          <Input 
            placeholder="user@example.com" 
            prefix={<UserOutlined />}
          />
        </Form.Item>

        <Form.Item
          name="roleId"
          label="Роль"
          rules={[{ required: true, message: 'Пожалуйста, выберите роль' }]}
        >
          <Select placeholder="Выберите роль">
            <Option value={1}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#d32f2f', fontWeight: '500' }}>Администратор</span>
              </div>
            </Option>
            <Option value={2}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#f57c00', fontWeight: '500' }}>Преподаватель</span>
              </div>
            </Option>
            <Option value={3}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#1976d2', fontWeight: '500' }}>Студент</span>
              </div>
            </Option>
          </Select>
        </Form.Item>

        <Form.Item>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>
              Отмена
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={<SaveOutlined />}
            >
              Сохранить
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default UserForm; 